/**
 * Annotators produce Annotations for the abstract text
 * @see Annotation
 * @see AnnotatedAbstract
 */
import * as _ from 'lodash'
import Abstract from './Abstract'
import { ReferencedTerms } from './extraction'
import {
	entireSelection,
	FOLLOW_UP_SENSES,
	GROUP_SENSES,
	RXNORM_ONTOLOGY_RE,
	termsRelated,
	DSNodeAnnotations
} from './DocSearch'
import Annotations, { Annotation, findSection, findSentence, TypedRange } from './Annotations'
import {
	BINARY_VALUE,
	COUNT_VALUE,
	GROUPN,
	PVALUE,
	QUANTIFIER,
	SD as SDScanner,
	CI as CIScanner,
	QUANTITY,
	QuantifierScanner,
	Scanner, CONT_VALUE, AVALUE, TIMEPOINT, DOSAGE, NCTIdLinkScanner
} from './Scanner'

const TAG_CLASS_MAP = {
	I: 'intervention',
	O: 'outcome',
	C: 'characteristic'
}

export const CLASS_SECTION_MAP = {
	intervention: ['methods', 'results'],
	outcome: ['results'],
	characteristic: null, //['background', 'objectives', 'methods'],
}

const SECTION_SENSES = {
	background: GROUP_SENSES.concat(FOLLOW_UP_SENSES),
	objectives: GROUP_SENSES.concat(FOLLOW_UP_SENSES),
	methods: GROUP_SENSES.concat(FOLLOW_UP_SENSES),
	results: GROUP_SENSES // sometimes group n is in the findings/results
}

export interface AnnotationLayer {
	name: string
	annotations: Annotation[]
}
export type AnnotationStack = { layers: AnnotationLayer[] } & { [name: string]: AnnotationLayer }

export abstract class Annotator {
	abstract annotate(abstract, contexts, stack: AnnotationStack, state?: any): AnnotationLayer
}

/**
 * Annotation parsing is performed in Abstract so just return the discovered annotations
 */
export class AbstractAnnotator extends Annotator {
	annotate(abstract, contexts, stack: AnnotationStack, state?: any): AnnotationLayer {
		return { name: 'section', annotations: abstract.abstract.annotations }
	}
}

export function createDocSearchAnnotations(docSearchResults: DSNodeAnnotations, text) {
	const annots: Annotation[] = []
	//const measures = _.keyBy(this.docSearchResults.measures, 'measure-id')
	for (const a of docSearchResults.annotations || []) {
		const [start, end] = entireSelection(a.selection)
		const terms = a.phrase
		const tagTypes = _.compact<string>(a.tags.map(t => TAG_CLASS_MAP[t]))
		const termTypes = []
		const allTypes = _.uniq(tagTypes.concat(termTypes))
		const type = tagTypes[0] || 'unknown'
		const annot: Annotation = {
			start,
			end,
			text: text.slice(start, end + 1),
			type,
			types: allTypes.length > 0 ? allTypes : [type],
			source: 'docsearch',
			terms,
			senses: _.compact(_.uniq(terms.flatMap(t => t.sense))),
		}
		annots.push(annot)
		// const mids = a['measure-ids']
		// if (mids && mids.length) {
		// 	annot.measures = createMeasures(annot, mids, measures, abstract.abstract)
		// 	annots.push(...annot.measures)
		// }
	}
	return annots
}

export function createDSAbstractAnnotations(docSearchResults: DSNodeAnnotations, abstract): Annotation[] {
	return createDocSearchAnnotations(docSearchResults, abstract.text).map(a => ({
		...a,
		section: abstract.findSection(a.start, a.end),
		sentence: abstract.findSentence(a.start, a.end)
	}))
}


function createMeasures(annot, mids, measures, abstract: Abstract) {
	return mids.map(mid => {
		const measure = measures[mid]
		const [start, end] = entireSelection(measure.selection)
		const text = abstract.text.slice(start, end + 1)
		return {
			start,
			end,
			measure,
			text,
			captured: text,
			type: 'measure',
			source: 'docsearch',
			parent: annot
		}
	})
}

export class DocSearchAnnotator extends Annotator {
	private annotations: Annotation[]
	constructor(abstract, annotations, referencedTerms?: ReferencedTerms) {
		super()
		let relatedSet
		if (referencedTerms) {
			// remove any docsearch annotations of given type that don't relate to the specified terms
			[annotations, relatedSet] = DocSearchAnnotator.filterApplicableAnnotations(annotations, referencedTerms, SECTION_SENSES)
		}
		// docsearch annotations may overlap amongst themselves;
		annotations = new Annotations(_.sortBy(annotations, a => a.start), true).removeOverlapping().getIntervals()
		DocSearchAnnotator.assignRelated(annotations)
		DocSearchAnnotator.assignContexts(abstract, annotations)
		this.annotations = DocSearchAnnotator.filterValidAnnotations(annotations, relatedSet)
	}
	annotate(abstract, contexts, stack: AnnotationStack, state?: any): AnnotationLayer {
		return { name: 'docsearch', annotations: this.annotations}
	}

	private static filterApplicableAnnotations(annotations: Annotation[], include: ReferencedTerms, senses = SECTION_SENSES) {
		const annotsByType = {}
		for (const a of annotations) {
			for (const type of a.types || []) {
				(annotsByType[type] || (annotsByType[type] = [])).push(a)
			}
		}
		const filtered = new Set<Annotation>()
		// accept all the annotations that are in included senses in allowed sections
		annotations.filter(a => {
			if (a.sentence) {
				const senses = _.compact(a.sentence?.types?.flatMap(t => SECTION_SENSES[t]))
				return senses.length > 0 && a.senses.some(s => senses.includes(s))
			}
		}).forEach(a => {
			filtered.add(a)
		})
		const types = ['intervention', 'outcome' , 'characteristic']
		const relatedAnnots = new Set<Annotation>()
		for (const type of types) {
			const terms = include[type + 's' as keyof ReferencedTerms] || []
			const annots = annotsByType[type] || []
			const related = []
			for (const term of terms) {
				related.push(annots.filter(a => {
					const related = a.terms.some(t => t.sense == term)
					//console.log(term, 'related to', annotationTerms, '? ', related)
					return related
				}))
			}

			// if a related annotation is present for EVERY search term of given type, then limit to those related annotations
			// otherwise we are missing a related annotation, so keep all the other candidates of the type to let the user choose
			//console.log('RELATED', type, related.flat(), related.flat().map(a => atos(a, {measures}, text)))
			//const unrelated = _.without(annots, related.flat())
			//console.log('UNRELATED', type, unrelated, unrelated.flat().map(a => atos(a, {measures}, text)))
			if (related.length > 0 && related.every(r => r.length > 0)) {
				related.flat().forEach(a => {
					relatedAnnots.add(a)
					filtered.add(a)
				})
			} else {
				annots.forEach(a => filtered.add(a))
			}
		}
		return [Array.from(filtered.values()), relatedAnnots]
	}

	/**
	 * established related annotations
	 */
	private static assignRelated(annotations: Annotation[]) {
		for (const a1 of annotations) {
			const related = []
			for (const a2 of annotations) {
				if (a1 === a2) continue
				if (_.some(a1.types, t => a2.types.includes(t))
					&& _.some(a1.terms, t => termsRelated(t, a2.terms))) {
					related.push(a2)
				}
			}
			a1.related = related
		}
	}

	/**
	 * Assign containing section to annotation, filtering out those in invalid sections for the annotation type
	 */
	private static assignContexts(abstract: Abstract, annotations: Annotation[]) {
		for (const annot of annotations) {
			annot.section = abstract.findSection(annot.start, annot.end)
			annot.sentence = abstract.findSentence(annot.start, annot.end)
			// if (!annot.section || !annot.sentence) {
			//   debugger
			// }
		}
	}

	private static filterValidAnnotations(annotations: Annotation[], relatedAnnots: Set<Annotation>) {
		const isOfType = (context, types) => context.types && context.types.some(t => types.includes(t))
		return annotations.filter(a => {
			const validSections = CLASS_SECTION_MAP[a.type]
			return !validSections || (relatedAnnots && relatedAnnots.has(a)) || (a.section && isOfType(a.section, validSections))
				|| (a.sentence && isOfType(a.sentence, validSections))
		})
	}
}

export class DocSearchAnnotationSelector extends Annotator {
	constructor(private readonly type: string) {
		super()
	}
	annotate(abstract, contexts, stack: AnnotationStack, state?: any): AnnotationLayer {
		const candidates = abstract.docsearchAnnotations.filter(a => a.types.includes(this.type))
		const withinContexts = candidates.filter(a =>
			contexts.some(c => a.start >= c.start && a.end <= c.end)
		)
		return { name: 'existing', annotations: withinContexts }
	}
}

/**
 * Returns non-overlapping ranges to scan further
 */
export type ContextFilter = (contexts, stack: AnnotationStack, state?: any) => TypedRange[]

export const SectionFilter = (sections?: string[]) =>  (contexts, stack: AnnotationStack) =>
	sections ? contexts.filter(c => {
		const section = findSection(c)
		return section && sections.includes(section.type)
	}) : contexts

// RXNORM drug doses from docsearch layer
export const RXNormAnnotations = (contexts, stack: AnnotationStack) =>
	stack.docsearch.annotations.filter((a: any) => a.terms && a.terms.some(t => RXNORM_ONTOLOGY_RE.test(t.uri)))

type FilterFn = (a) => boolean

const TypeFilter = (...types) => (a: any) =>
	(a.type && types.includes(a.type)) ||
	a.types && a.types.some(t => types.includes(t))

export const InAnnotatedSentences = (...filters: FilterFn[]) => (contexts, stack: AnnotationStack) => {
	const annotationsOfType = stack.layers.flatMap(l =>
		l.annotations.filter(a => filters.every(f => f(a)))
	)
	const sentences = _.uniq(annotationsOfType.map(a => a.sentence))
	return contexts.filter(c => sentences.includes(findSentence(c)))
}

export const InInterventionSentences = InAnnotatedSentences(TypeFilter('intervention'))
export const InOutcomeSentences = InAnnotatedSentences(TypeFilter('outcome'))
export const InRelatedOutcomeSentences = (serializedPhrases = []) => InAnnotatedSentences(TypeFilter('outcome'), a => {
	// TODO: test if a is related to outcome termIds
	return serializedPhrases.length > 0
})
export const InInterventionOrOutcomeSentences = InAnnotatedSentences(TypeFilter('intervention', 'outcome'))
export const InResultsSection = SectionFilter(['results'])

// return sections in which annotations of a particular class have been found
// const ClassSectionFilter = (classes?: string[]) => {
//   return (abstract: Abstract, contexts, stack: AnnotationStack) => {
//     const layers = classes.map(c => stack[c])
//     return _.uniq(_.compact(layers.flatMap(layer => layer.annotations.map((a: any) => a.section))))
//   }
// }

export class LocalAnnotator extends Annotator {
	constructor(protected readonly scanner: Scanner) {
		super()
	}

	annotate(abstract, contexts, stack: AnnotationStack, state?: any): AnnotationLayer {
		const scanner = this.scanner
		const annotations = new Annotations()
		for (const context of contexts) {
			annotations.insert(...scanner.scan(context, 'local'))
		}
		annotations.sort()
		return { name: 'local:' + scanner.type, annotations: annotations.getIntervals() }
	}
}

export class NCTIdAnnotator extends LocalAnnotator {
	protected readonly scanner = NCTIdLinkScanner
}

export class QuantifierAnnotator extends LocalAnnotator {
	constructor(type?, private readonly unitFilter?) {
		super(new QuantifierScanner(type))
	}

	annotate(abstract, contexts, stack: AnnotationStack, state?: any): AnnotationLayer {
		const layer = super.annotate(abstract, contexts, stack, state)
		if (this.unitFilter) {
			layer.annotations = layer.annotations.filter(a => {
				const bounds = _.compact([a.parsed.quantifier.High, a.parsed.quantifier.Low])
				return bounds.length && bounds.every(b => this.unitFilter.test(b.unit || ''))
			})
		}
		return layer
	}
}
export class RxNormQuantifiers extends LocalAnnotator {
	constructor() {
		super(QUANTIFIER)
	}

	annotate(abstract, contexts, stack: AnnotationStack, state?: any): AnnotationLayer {
		const layer = super.annotate(abstract, contexts, stack, state)
		layer.annotations.forEach(a => a.visual = false)
		return layer
	}
}

const followupAnnotations = (a: any) =>
	a.senses && a.senses.some(s => FOLLOW_UP_SENSES.includes(s))

const MethodSectionContexts = SectionFilter(['methods'])

export const MethodsFollowupSentences = (contexts, stack: AnnotationStack, state?: any) => {
	return _.uniq(MethodSectionContexts(stack.docsearch.annotations, stack).filter(followupAnnotations).map(a => a.sentence))
}

export class TimePointAnnotator extends LocalAnnotator {
	constructor() {
		super(TIMEPOINT)
	}
	// annotate(abstract, contexts, stack: AnnotationStack, state: any = {}): AnnotationLayer {
	// 	const layer = super.annotate(abstract, contexts, stack, state)
	// 	const dsAnnotations = new Annotations(stack.docsearch.annotations)
	// 	// prioritize our time point quantifiers over docsearch measures
	// 	layer.annotations.forEach(a => {
	// 		a.visual = true
	// 		const overlapped = dsAnnotations.findOverlaps(a)
	// 		for (const other of overlapped) {
	// 			if (other.type == 'measure') {
	// 				other.visual = false
	// 				other.parent.measures = _.without(other.parent.measures, other)
	// 				other.parent = undefined
	// 			}
	// 		}
	// 	})
	// 	return layer
	// }
}

export class DosageAnnotator extends LocalAnnotator {
	constructor() {
		super(DOSAGE)
	}
}

export class GroupN extends LocalAnnotator {
	constructor() {
		super(GROUPN)
	}
}

export class SD extends LocalAnnotator {
	constructor() {
		super(SDScanner)
	}
}

export class CI extends LocalAnnotator {
	constructor() {
		super(new CIScanner())
	}
}

export class Quantifier extends LocalAnnotator {
	constructor() {
		super(QUANTIFIER)
	}
}

export class BinaryValue extends LocalAnnotator {
	constructor() {
		super(BINARY_VALUE)
	}
}

export class CountValue extends LocalAnnotator {
	constructor() {
		super(COUNT_VALUE)
	}
}

export class ContinuousValue extends LocalAnnotator {
	constructor() {
		super(CONT_VALUE)
	}
}

export class PValue extends LocalAnnotator {
	constructor() {
		super(PVALUE)
	}
}

export class AValue extends LocalAnnotator {
	constructor() {
		super(AVALUE)
	}
}

export class Quantity extends LocalAnnotator {
	constructor() {
		super(QUANTITY)
	}
}

const ANNOTATION_TYPES = {
	dosage: new DosageAnnotator(),
	timepoint: [new TimePointAnnotator(), new Quantity()],
	'group-n': [new GroupN(), new Quantity()],
	'binary-value': new BinaryValue(),
	'count-value': new CountValue(),
	'continuous-value': [new ContinuousValue(), new Quantity()],
	pvalue: new PValue(),
	intervention: [new DocSearchAnnotationSelector('intervention')],
	outcome: [new DocSearchAnnotationSelector('outcome')],
	ci: new CI(),
	sd: [new SD(), new Quantity()],
	se: [new SD(), new Quantity()]
}

export const userSelectable = (type) => type in ANNOTATION_TYPES

export const annotatorsFor = (types = []): Annotator[] =>
	types.map(t => ANNOTATION_TYPES[t]).flat()